<template>
  <b-modal
    id="qr-enroll"
    ref="qrEnroll"
    title="Kích hoạt thiết bị xác thực"
    size="lg"
    centered
    no-close-on-backdrop
    no-close-on-esc
    @hidden="hideModal"
  >
    <div class="mb-1 qr-enroll otp-scope">
      <div class="title font-weight-bold">Kích hoạt thiết bị OTP bằng mã QR</div>
      <div class="sub-title mt-1 fs-14px">
        Vui lòng sử dụng ứng dụng eDoc trên Mobile/Tablet để quét mã QR bên dưới và kích hoạt thiết bị xác thực 2 bước.
      </div>

      <!-- 1847: bước xác minh email TRƯỚC khi cấp QR (khi guard BE trả EMAIL_UNVERIFIED). Cờ OFF -> KHÔNG hiện. -->
      <div v-if="needVerify" class="content mt-2 verify-email">
        <div class="font-weight-bold">Xác minh email trước khi cấp mã QR</div>
        <p class="text-muted mt-1 fs-14px">
          Bạn cần xác minh email trước khi kích hoạt thiết bị. Mã 6 số sẽ được gửi tới email đã đăng ký.
        </p>
        <b-button
          variant="outline-primary"
          size="sm"
          class="mb-2"
          :disabled="isLoading || sendCount >= maxSendCount"
          @click="requestCode"
        >{{ sendCount > 0 ? 'Gửi lại mã' : 'Gửi mã về email' }}</b-button>
        <div v-if="sendCount >= maxSendCount" class="text-muted fs-14px mb-2">
          Đã đạt số lần gửi mã tối đa. Vui lòng thử lại sau ít phút.
        </div>
        <div class="mb-1 font-weight-bold">Mã xác minh</div>
        <b-form-input
          v-model="verifyCode"
          type="text"
          inputmode="numeric"
          maxlength="6"
          class="outline-primary input"
          placeholder="Mã 6 số"
          @keyup.enter="verifyEmail"
        />
        <div class="d-flex mt-2">
          <base-button
            button="Xác minh"
            :disabled="isLoading || !verifyCode"
            @click="verifyEmail"
          />
        </div>
        <p class="text-muted mt-1 fs-14px">Không nhận được mã? Liên hệ quản trị viên.</p>
      </div>

      <!-- Bước 1 QR: loại trừ !activated - sau confirmBinding pending=null, nếu chỉ
           check !pending thì bước 1 sống lại -> hiện đồng thời QR + ô OTP (bước 3). -->
      <div v-if="!needVerify && ticketId && !pending && !activated" class="content mt-2 text-center">
        <p class="mb-1">
          1. Mở <b>ứng dụng eDoc Mobile</b> - chọn <b>Kích hoạt bằng QR</b> - quét mã dưới đây:
        </p>
        <div class="qr-box">
          <qrcode-vue :value="qrValue" :size="220" level="M" />
        </div>
        <p class="mt-1 text-muted fs-14px">
          Mã QR còn hiệu lực trong <b>{{ remaining }}</b> giây.
        </p>
        <b-button
          v-if="remaining <= 0"
          variant="outline-primary"
          size="sm"
          @click="mintTicket"
        >Tạo mã QR mới</b-button>
      </div>

      <div v-if="pending" class="content mt-2">
        <p class="mb-1">
          2. Thiết bị <b>{{ pending.tenThietBi }}</b> vừa quét mã QR. Nếu đúng là bạn,
          hãy xác nhận để kích hoạt thiết bị này.
        </p>
        <div class="d-flex">
          <base-button
            button="Xác nhận kích hoạt thiết bị này"
            :disabled="isLoading"
            @click="confirmBinding"
          />
        </div>
      </div>

      <div v-if="activated" class="content mt-2">
        <p class="mb-1">
          Nhập mã OTP từ ứng dụng eDoc Mobile/Tablet để kích hoạt và đăng nhập.
        </p>
        <b-form-input
          v-otp-autofocus
          v-model="otp"
          type="text"
          inputmode="numeric"
          maxlength="6"
          autocomplete="one-time-code"
          pattern="[0-9]*"
          class="outline-primary input"
          placeholder="Mã OTP"
          @keyup.enter="submitOtp"
        />
      </div>
    </div>

    <template v-slot:modal-footer>
      <div class="w-100 text-right">
        <base-button
          v-if="activated"
          button="Đăng nhập"
          :disabled="!otp"
          @click="submitOtp"
        />
        <b-button variant="outline-primary" class="ml-1" @click="hideModal()">Đóng</b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
  import QrcodeVue from 'qrcode.vue'
  import { DOMAIN, API_VERSION } from '@/constants/constants'

  export default {
    name: 'QrEnrollModal',
    components: { QrcodeVue },
    data() {
      return {
        ticketId: null,
        remaining: 0,
        pending: null,
        activated: false,
        activatedDeviceId: null, // giữ deviceId đã Active để submitOtp verify Device.Secret
        otp: null,
        isLoading: false,
        pollTimer: null,
        countdownTimer: null,
        // 1847: bước xác minh email trước cấp QR (guard BE EMAIL_UNVERIFIED).
        needVerify: false,
        verifyCode: null,
        sendCount: 0, // đếm số lần gửi mã trong phiên (cap = maxSendCount, khớp IssueMax BE).
        maxSendCount: 3,
      }
    },
    computed: {
      // Nội dung QR: {t: ticketId, u: common-be public base URL KÈM /api}. App ghép u + path controller
      // -> u PHẢI có /api (DOMAIN không kèm, API_VERSION="/api"). App allowlist host (Phase 4).
      qrValue() {
        return JSON.stringify({ t: this.ticketId, u: DOMAIN + API_VERSION })
      },
    },
    methods: {
      resetData() {
        this.ticketId = null
        this.remaining = 0
        this.pending = null
        this.activated = false
        this.activatedDeviceId = null
        this.otp = null
        this.needVerify = false
        this.verifyCode = null
        this.sendCount = 0
        this.stopTimers()
      },
      stopTimers() {
        if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null }
        if (this.countdownTimer) { clearInterval(this.countdownTimer); this.countdownTimer = null }
      },
      // 1847: đọc code lỗi tolerant casing (camelCase common-fe hiện dùng; PascalCase phòng drift serializer).
      errCode(payload) {
        const d = payload && (payload.data || payload)
        return d && (d.code || d.Code)
      },
      errMessage(payload) {
        const d = payload && (payload.data || payload)
        return (d && (d.message || d.Message)) || ''
      },
      mintTicket() {
        this.isLoading = true
        this.pending = null
        this.activated = false
        this.$store.dispatch('common/mintEnrollTicket')
            .then((res) => {
              this.isLoading = false
              // common-fe action resolve response.data (ServiceResponse camelCase {data, succeeded, message}).
              if (res && res.succeeded && res.data) {
                this.needVerify = false
                this.ticketId = res.data.ticketId
                this.remaining = res.data.expiresInSeconds || 180
                this.startCountdown()
                this.startPolling()
              } else {
                this.$toasted.global.showErrorMessage({ message: 'Không tạo được mã QR. Vui lòng thử lại.' })
              }
            })
            .catch((err) => {
              this.isLoading = false
              // 1847: guard email BE -> HTTP 400 code EMAIL_UNVERIFIED (qua .catch, KHÔNG .then). Rẽ sang bước email.
              // Remint sau khi marker đã tiêu (QR hết hạn -> "Tạo mã QR mới") CŨNG rơi vào đây -> hiện lại bước email.
              if (this.errCode(err) === 'EMAIL_UNVERIFIED') {
                this.needVerify = true
                this.ticketId = null
                this.stopTimers()
                return
              }
              // Predicate first-device (1956) reject: message "đã có thiết bị, dùng Thêm thiết bị" (KHÔNG code) -> hiện rõ, KHÔNG vào bước email.
              const msg = this.errMessage(err)
              if (this.isSessionExpired(err)) { this.handleSessionExpired(); return }
              this.$toasted.global.showErrorMessage({ message: msg || 'Không tạo được mã QR. Vui lòng thử lại.' })
            })
      },
      // 1847: user tự yêu cầu gửi mã kích hoạt về email (reuse action ModalAuthenticator). Cap sendCount.
      requestCode() {
        if (this.sendCount >= this.maxSendCount) return
        this.isLoading = true
        this.$store.dispatch('common/requestActivationSelf')
            .then((res) => {
              this.isLoading = false
              if (res && res.succeeded) {
                this.sendCount += 1
                this.$toasted.global.showSuccessMessage({ message: 'Đã gửi mã xác minh tới email đã đăng ký.' })
              } else {
                this.$toasted.global.showErrorMessage({ message: (res && res.message) || 'Không gửi được mã. Vui lòng thử lại.' })
              }
            })
            .catch((err) => {
              this.isLoading = false
              this.$toasted.global.showErrorMessage({ message: this.errMessage(err) || 'Có lỗi xảy ra khi gửi mã.' })
            })
      },
      // 1847: verify mã email -> BE đặt marker per-uid -> tự retry mintTicket (lần này qua guard -> QR).
      verifyEmail() {
        if (!this.verifyCode) return
        this.isLoading = true
        this.$store.dispatch('common/verifyActivation', { code: String(this.verifyCode).trim() })
            .then((res) => {
              this.isLoading = false
              if (res && res.succeeded) {
                this.needVerify = false
                this.verifyCode = null
                this.mintTicket() // marker vừa đặt -> mint qua guard -> hiện QR.
              } else {
                this.$toasted.global.showErrorMessage({ message: (res && res.message) || 'Mã không đúng hoặc đã hết hạn.' })
              }
            })
            .catch((err) => {
              this.isLoading = false
              this.$toasted.global.showErrorMessage({ message: this.errMessage(err) || 'Có lỗi xảy ra khi xác minh.' })
            })
      },
      startCountdown() {
        this.stopCountdown()
        this.countdownTimer = setInterval(() => {
          if (this.remaining > 0) this.remaining -= 1
          else this.stopCountdown()
        }, 1000)
      },
      stopCountdown() {
        if (this.countdownTimer) { clearInterval(this.countdownTimer); this.countdownTimer = null }
      },
      startPolling() {
        if (this.pollTimer) clearInterval(this.pollTimer)
        this.pollTimer = setInterval(() => this.pollPending(), 3000)
      },
      pollPending() {
        if (this.activated || this.pending) return
        this.$store.dispatch('common/getPendingEnroll')
            .then((res) => {
              if (res && res.data && res.data.hasPending) {
                this.pending = { deviceId: res.data.deviceId, tenThietBi: res.data.tenThietBi }
                if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null }
                this.stopCountdown()
              }
            })
            .catch(() => { /* poll best-effort */ })
      },
      // Token stage-1 TTL 10' hết hạn giữa luồng enroll dài -> common-be 401 "hết hạn". Nhận diện
      // để báo rõ + đóng modal về login (thay vì "Thao tác thất bại" mơ hồ).
      isSessionExpired(payload) {
        const d = payload && (payload.data || payload)
        const text = String((d && (d.message || (d.Messages && d.Messages.join(' ')))) || '')
        return /hết hạn|het han|accesstoken|unauthorized|401/i.test(text)
      },
      handleSessionExpired() {
        this.$toasted.global.showErrorMessage({
          message: 'Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại để kích hoạt thiết bị.',
        })
        this.hideModal()
      },
      confirmBinding() {
        if (!this.pending) return
        this.isLoading = true
        this.$store.dispatch('common/confirmEnrollBinding', { deviceId: this.pending.deviceId })
            .then((res) => {
              this.isLoading = false
              if (res && res.succeeded) {
                this.activatedDeviceId = this.pending.deviceId // giữ trước khi xoá: submitOtp verify Device.Secret
                this.activated = true
                this.pending = null
                this.stopTimers() // dừng poll + countdown: đã sang bước 3 (nhập OTP)
                this.$toasted.global.showSuccessMessage({ message: 'Đã kích hoạt thiết bị. Vui lòng nhập mã OTP để đăng nhập.' })
              } else if (this.isSessionExpired(res)) {
                this.handleSessionExpired()
              } else {
                this.$toasted.global.showErrorMessage({ message: (res && res.message) || 'Xác nhận thất bại. Vui lòng thử lại.' })
              }
            })
            .catch((err) => {
              this.isLoading = false
              if (this.isSessionExpired(err && err.response)) { this.handleSessionExpired(); return }
              this.$toasted.global.showErrorMessage({ message: 'Xác nhận thất bại. Vui lòng thử lại.' })
            })
      },
      submitOtp() {
        if (!this.otp) return
        this.isLoading = true
        this.$store.dispatch('common/validateLogin', { twoFactorCode: this.otp, deviceId: this.activatedDeviceId })
            .then((res) => {
              this.isLoading = false
              if (res && res.succeeded) {
                this.hideModal()
                this.$emit('authenticator', res)
              } else if (this.isSessionExpired(res)) {
                this.handleSessionExpired()
              } else {
                this.$toasted.global.showErrorMessage({ message: (res && res.message) || 'Mã OTP không đúng.' })
              }
            })
            .catch((err) => {
              this.isLoading = false
              if (this.isSessionExpired(err && err.response)) { this.handleSessionExpired(); return }
              this.$toasted.global.showErrorMessage({ message: 'Thao tác thất bại.' })
            })
      },
      async showModal() {
        this.resetData()
        await this.$refs.qrEnroll.show()
        this.mintTicket()
      },
      hideModal() {
        this.stopTimers()
        this.$refs.qrEnroll.hide()
        this.$emit('hideModal')
        this.resetData()
      },
    },
    beforeDestroy() {
      this.stopTimers()
    },
  }
</script>

<style lang="scss" scoped>
  // Reskin OTP (Phase 3): overlay token. Giữ toàn bộ script + camelCase reader (RT-3) + 3 bước QR.
  .qr-enroll {
    font-family: var(--otp-sans);
    color: var(--otp-ink);
    .title { font-size: 16px; font-family: var(--otp-display); color: var(--otp-ink); }
    .fs-14px { font-size: 14px; }
    .mt-1 { margin-top: 1rem !important; }
    .mt-2 { margin-top: 1rem !important; }
    .mb-1 { margin-bottom: 1rem !important; }
    .ml-1 { margin-left: 1rem !important; }
    .qr-box {
      display: inline-block;
      padding: 14px;
      background: #fff; // QR luôn nền trắng để máy quét đọc được, kể cả dark-mode.
      border: 1px solid var(--otp-border-strong);
      border-radius: var(--otp-radius);
      box-shadow: var(--otp-shadow-sm);
    }
    .text-muted { color: var(--otp-muted); }
    .input {
      border: 1px solid var(--otp-border-strong);
      border-radius: var(--otp-radius-sm);
      height: 36px;
      max-width: 50%;
      &:focus { border-color: var(--otp-blue); box-shadow: var(--otp-ring); outline: none; }
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
      &[type='number'] { -moz-appearance: textfield; }
    }
  }
</style>
